import { useEffect, useState, type RefObject } from 'react';
import { formatInteger } from '@brimo/shared';
import { Button, Checkbox, Eyebrow, FilterChip, Popover, Select, TextLink } from '../../../ds/components';
import { useBoardPreview, useDealFilters } from '../api';
import type { DealFilter } from '../types';
import styles from './board.module.css';

/**
 * Deal-Pipeline 07: the filter flyout (Berater, Objekt, Tippgeber-Partner (D-248), Zustand, Stage-Alter) with the live count
 * „ergibt N Deals"; „Anwenden" commits, „zurücksetzen" clears the pending filters.
 */

export interface FilterFlyoutProps {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  filter: DealFilter;
  onClose: () => void;
  onApply: (filter: DealFilter) => void;
}

function cleanPending(filter: DealFilter): DealFilter {
  return {
    q: filter.q,
    advisorId: filter.advisorId,
    propertyId: filter.propertyId,
    referrerPartnerId: filter.referrerPartnerId,
    gateOnly: filter.gateOnly,
    overdueOnly: filter.overdueOnly,
    noNextStep: filter.noNextStep,
    includeCancelled: filter.includeCancelled,
    stageAgeOver: filter.stageAgeOver,
  };
}

export function FilterFlyout({ open, anchorRef, filter, onClose, onApply }: FilterFlyoutProps) {
  const [pending, setPending] = useState<DealFilter>(() => cleanPending(filter));
  const options = useDealFilters(open);
  const preview = useBoardPreview(pending, open);

  useEffect(() => {
    if (open) setPending(cleanPending(filter));
  }, [open, filter]);

  const count = preview.data?.summary.filteredDeals ?? null;

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      placement="bottom-end"
      width={340}
      variant="none"
      className={styles.flyoutPanel}
      label="Filter"
      initialFocus="first"
      offset={8}
    >
      <div className={styles.flyout}>
        <div className={styles.flyoutHead}>
          <span className={styles.flyoutTitle}>Filter</span>
          <TextLink
            as="button"
            size="small"
            tone="muted"
            className={styles.flyoutReset}
            onClick={() => setPending({ q: filter.q })}
          >
            zurücksetzen
          </TextLink>
        </div>
        <div className={styles.flyoutBody}>
          <div className={styles.flyoutSection}>
            <Eyebrow as="div">Berater</Eyebrow>
            <Select
              label="Berater"
              labelHidden
              size="compact"
              value={pending.advisorId ?? ''}
              options={[
                { value: '', label: 'Alle Berater' },
                ...(options.data?.advisors ?? []).map((user) => ({ value: user.id, label: user.name })),
              ]}
              onChange={(event) => setPending({ ...pending, advisorId: event.target.value || undefined })}
            />
          </div>
          <div className={styles.flyoutSection}>
            <Eyebrow as="div">Objekt</Eyebrow>
            <Select
              label="Objekt"
              labelHidden
              size="compact"
              value={pending.propertyId ?? ''}
              options={[
                { value: '', label: 'Alle Objekte' },
                ...(options.data?.properties ?? []).map((property) => ({
                  value: property.id,
                  label: property.name,
                })),
              ]}
              onChange={(event) => setPending({ ...pending, propertyId: event.target.value || undefined })}
            />
          </div>
          {(options.data?.referrerPartners?.length ?? 0) > 0 || pending.referrerPartnerId ? (
            <div className={styles.flyoutSection}>
              <Eyebrow as="div">Tippgeber-Partner</Eyebrow>
              <Select
                label="Tippgeber-Partner"
                labelHidden
                size="compact"
                value={pending.referrerPartnerId ?? ''}
                options={[
                  { value: '', label: 'Alle Tippgeber-Partner' },
                  ...(options.data?.referrerPartners ?? []).map((partner) => ({
                    value: partner.id,
                    label: partner.active ? partner.name : `${partner.name} (inaktiv)`,
                  })),
                ]}
                onChange={(event) =>
                  setPending({ ...pending, referrerPartnerId: event.target.value || undefined })
                }
              />
            </div>
          ) : null}
          <div className={styles.flyoutSection}>
            <Eyebrow as="div">Zustand</Eyebrow>
            <div className={styles.flyoutChecks}>
              <Checkbox
                label="nur mit Gate-Warnung"
                checked={Boolean(pending.gateOnly)}
                onChange={(event) => setPending({ ...pending, gateOnly: event.target.checked || undefined })}
              />
              <Checkbox
                label="nur überfällig"
                checked={Boolean(pending.overdueOnly)}
                onChange={(event) =>
                  setPending({ ...pending, overdueOnly: event.target.checked || undefined })
                }
              />
              <Checkbox
                label="ohne nächsten Schritt"
                checked={Boolean(pending.noNextStep)}
                onChange={(event) =>
                  setPending({ ...pending, noNextStep: event.target.checked || undefined })
                }
              />
              <Checkbox
                label="stornierte einblenden"
                checked={Boolean(pending.includeCancelled)}
                onChange={(event) =>
                  setPending({ ...pending, includeCancelled: event.target.checked || undefined })
                }
              />
            </div>
          </div>
          <div className={styles.flyoutSection}>
            <Eyebrow as="div">Stage-Alter</Eyebrow>
            <div className={styles.flyoutPills} role="group" aria-label="Stage-Alter">
              {(
                options.data?.stageAgeOptions ?? [
                  { key: 'all', label: 'alle' },
                  { key: '14', label: '> 14 T' },
                  { key: '30', label: '> 30 T' },
                ]
              ).map((option) => (
                <FilterChip
                  key={option.key}
                  label={option.label}
                  variant="dark"
                  active={(pending.stageAgeOver ?? 'all') === option.key}
                  onClick={() =>
                    setPending({ ...pending, stageAgeOver: option.key === 'all' ? undefined : option.key })
                  }
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.flyoutFoot}>
          <span className={styles.flyoutCount} aria-live="polite">
            {count === null ? '…' : `ergibt ${formatInteger(count)} ${count === 1 ? 'Deal' : 'Deals'}`}
          </span>
          <span className={styles.flyoutActions}>
            <Button variant="secondary" size="small" onClick={onClose}>
              Abbrechen
            </Button>
            <Button variant="dark" size="small" onClick={() => onApply(pending)}>
              Anwenden
            </Button>
          </span>
        </div>
      </div>
    </Popover>
  );
}
